import {
  ChangeDetectionStrategy,
  Component,
  computed,
  effect,
  input,
  OnDestroy,
} from '@angular/core';
import { Subject, takeUntil, timer } from 'rxjs';
import { MatIcon } from '@angular/material/icon';
import { IIcon } from '@core/interfaces/icon.interface';
import { ButtonIconComponent } from '@core/components/buttons/button-icon/button-icon.component';
import { NgClass } from '@angular/common';
import { transition, trigger, useAnimation } from '@angular/animations';
import { zoomInAnimation } from '@core/animations/zoom-in.animation';
import { fadeOutAnimation } from '@core/animations/fade-out.animation';
import { APP_CONST } from '@core/constants/app.const';
import { NotificationVariant } from '@core/enums/notifications/notification-variant.enum';

@Component({
  selector: 'cgw-notification',
  standalone: true,
  imports: [
    // Components
    ButtonIconComponent,
    // Directives
    MatIcon,
    NgClass,
  ],
  templateUrl: './notification.component.html',
  styleUrl: './notification.component.scss',
  host: {
    style: 'align-self: center; pointer-events: auto;',
    '[@showDismiss]': '',
  },
  changeDetection: ChangeDetectionStrategy.OnPush,
  animations: [
    trigger('showDismiss', [
      transition(':enter', [useAnimation(zoomInAnimation)]),
      transition(':leave', [useAnimation(fadeOutAnimation)]),
    ]),
  ],
})
export class NotificationComponent implements OnDestroy {
  //#region I/O
  public readonly id = input.required<string>();
  public readonly text = input.required<string>();
  public readonly variant = input<NotificationVariant>(NotificationVariant.SUCCESS);
  public readonly duration = input<number | null>(APP_CONST.notificationDuration);
  public readonly closable = input<boolean>(false);
  public readonly closeCallback = input<(id: string) => void>((_) => {});
  public readonly icon = input<IIcon | null>(null);
  //#endregion

  private readonly _autoDismissResetS = new Subject<void>();

  public readonly isError = computed<boolean>(() => this.variant() == NotificationVariant.ERROR);

  constructor() {
    this.autoDismiss();
  }

  public close(): void {
    if (!this.closeCallback()) return;

    this.closeCallback()(this.id());
  }

  public ngOnDestroy(): void {
    this._autoDismissResetS.next();
    this._autoDismissResetS.complete();
  }

  private autoDismiss(): void {
    effect(() => {
      this._autoDismissResetS.next();
      if (this.duration() == null) return;

      timer(this.duration())
        .pipe(takeUntil(this._autoDismissResetS))
        .subscribe(() => {
          this.close();
        });
    });
  }
}
